'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AmortizationCalculator() {
  const t = useTranslations("tools.amortizationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    loanAmount: 200000,
    loanYears: 15,
    loanMonths: 0,
    interestRate: 6,
    extraPaymentsEnabled: false,
    startMonth: 11,
    startYear: 2025,
    extraMonthly: 0,
    extraYearly: 0,
    extraOnetime: 0,
    onetimeMonth: 11,
    onetimeYear: 2026,
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    calculateAmortization();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const calculateAmortization = () => {
    const loanAmount = inputs.loanAmount;
    const totalMonths = inputs.loanYears * 12 + inputs.loanMonths;

    if (totalMonths === 0) return;

    const monthlyRate = inputs.interestRate / 100 / 12;
    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);

    let balance = loanAmount;
    let totalInterest = 0;
    let totalPrincipal = 0;
    const schedule = [];

    let currentDate = new Date(inputs.startYear, inputs.startMonth, 1);
    let paymentNum = 1;

    while (balance > 0.01 && paymentNum <= totalMonths * 2) {
      const interestPayment = balance * monthlyRate;
      let principalPayment = monthlyPayment - interestPayment;
      let extraPayment = inputs.extraPaymentsEnabled ? inputs.extraMonthly : 0;

      if (inputs.extraPaymentsEnabled && currentDate.getMonth() === 0) {
        extraPayment += inputs.extraYearly;
      }

      if (inputs.extraPaymentsEnabled && currentDate.getFullYear() === inputs.onetimeYear && currentDate.getMonth() === inputs.onetimeMonth && inputs.extraOnetime > 0) {
        extraPayment += inputs.extraOnetime;
      }

      principalPayment += extraPayment;

      if (principalPayment > balance) {
        principalPayment = balance;
      }

      const totalPayment = interestPayment + principalPayment;
      balance -= principalPayment;

      totalInterest += interestPayment;
      totalPrincipal += principalPayment;

      schedule.push({
        paymentNum: paymentNum,
        date: new Date(currentDate),
        payment: totalPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });

      currentDate.setMonth(currentDate.getMonth() + 1);
      paymentNum++;

      if (balance < 0.01) break;
    }

    setResults({
      monthlyPayment,
      totalPrincipal,
      totalInterest,
      totalPaid: totalPrincipal + totalInterest,
      payoffDate: schedule.length > 0 ? schedule[schedule.length - 1].date : null,
      schedule
    });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    calculateAmortization();
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatDate = (date: Date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[date.getMonth()] + ' ' + date.getFullYear();
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', textAlign: 'center' as const },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem', marginBottom: '2rem' },
    { padding: '2.5rem', borderRadius: '1.5rem', marginBottom: '2.5rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1rem' },
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'border-color 0.3s',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const inputRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.75rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '1rem' }
  );

  const checkboxGroupStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      background: '#f8fafc',
      borderRadius: '0.5rem'
    },
    { padding: '0.875rem', margin: '1rem 0' },
    { padding: '1rem', margin: '1.25rem 0' },
    { padding: '1rem', margin: '1.5rem 0' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.125rem' },
    { padding: '1.125rem', fontSize: '1.2rem' }
  );

  const summaryItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
      marginBottom: '0.75rem'
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const monthOptions = [
    { value: 0, label: t("monthJanuary") },
    { value: 1, label: t("monthFebruary") },
    { value: 2, label: t("monthMarch") },
    { value: 3, label: t("monthApril") },
    { value: 4, label: t("monthMay") },
    { value: 5, label: t("monthJune") },
    { value: 6, label: t("monthJuly") },
    { value: 7, label: t("monthAugust") },
    { value: 8, label: t("monthSeptember") },
    { value: 9, label: t("monthOctober") },
    { value: 10, label: t("monthNovember") },
    { value: 11, label: t("monthDecember") }
  ];

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <h2 style={h2Style}>{t("toolTitle")}</h2>

          <div style={calculatorLayoutStyle}>
            <div style={glassCardStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="loan-amount">{t("loanAmountLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="loan-amount"
                    value={inputs.loanAmount}
                    onChange={(e) => setInputs({...inputs, loanAmount: parseFloat(e.target.value) || 0})}
                    min="0"
                    step="1000"
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("loanTermLabel")}</label>
                  <div style={inputRowStyle}>
                    <input
                      style={inputStyle}
                      type="number"
                      value={inputs.loanYears}
                      onChange={(e) => setInputs({...inputs, loanYears: parseInt(e.target.value) || 0})}
                      min="0"
                      max="50"
                      placeholder={t("yearsPlaceholder")}
                    />
                    <input
                      style={inputStyle}
                      type="number"
                      value={inputs.loanMonths}
                      onChange={(e) => setInputs({...inputs, loanMonths: parseInt(e.target.value) || 0})}
                      min="0"
                      max="11"
                      placeholder={t("monthsPlaceholder")}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest-rate">{t("interestRateLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="interest-rate"
                    value={inputs.interestRate}
                    onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                    min="0"
                    max="30"
                    step="0.01"
                    required
                  />
                </div>

                <div style={checkboxGroupStyle}>
                  <input
                    type="checkbox"
                    id="extra-payments-toggle"
                    checked={inputs.extraPaymentsEnabled}
                    onChange={(e) => setInputs({...inputs, extraPaymentsEnabled: e.target.checked})}
                    style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  <label htmlFor="extra-payments-toggle" style={{ margin: 0, cursor: 'pointer', fontWeight: 600, color: '#2563eb' }}>
                    {t("extraPaymentsToggle")}
                  </label>
                </div>

                {inputs.extraPaymentsEnabled && (
                  <div style={{
                    ...glassCardStyle,
                    background: '#f1f5f9',
                    padding: screenSize === 'mobile' ? '1rem' : '1.5rem',
                    marginTop: '1rem'
                  }}>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t("loanStartDateLabel")}</label>
                      <div style={inputRowStyle}>
                        <select
                          style={inputStyle}
                          value={inputs.startMonth}
                          onChange={(e) => setInputs({...inputs, startMonth: parseInt(e.target.value)})}
                        >
                          {monthOptions.map(month => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                        <input
                          style={inputStyle}
                          type="number"
                          value={inputs.startYear}
                          onChange={(e) => setInputs({...inputs, startYear: parseInt(e.target.value) || 2025})}
                          min="2000"
                          max="2100"
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #cbd5e1' }}>
                      <label style={{ ...labelStyle, fontSize: '0.9rem', color: '#475569' }}>{t("extraMonthlyLabel")}</label>
                      <input
                        style={inputStyle}
                        type="number"
                        value={inputs.extraMonthly}
                        onChange={(e) => setInputs({...inputs, extraMonthly: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                        placeholder="$0"
                      />
                    </div>

                    <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #cbd5e1' }}>
                      <label style={{ ...labelStyle, fontSize: '0.9rem', color: '#475569' }}>{t("extraYearlyLabel")}</label>
                      <input
                        style={inputStyle}
                        type="number"
                        value={inputs.extraYearly}
                        onChange={(e) => setInputs({...inputs, extraYearly: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                        placeholder="$0"
                      />
                    </div>

                    <div>
                      <label style={{ ...labelStyle, fontSize: '0.9rem', color: '#475569' }}>{t("oneTimePaymentLabel")}</label>
                      <input
                        style={inputStyle}
                        type="number"
                        value={inputs.extraOnetime}
                        onChange={(e) => setInputs({...inputs, extraOnetime: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="100"
                        placeholder="$0"
                      />
                      <div style={{ ...inputRowStyle, marginTop: '0.5rem' }}>
                        <select
                          style={inputStyle}
                          value={inputs.onetimeMonth}
                          onChange={(e) => setInputs({...inputs, onetimeMonth: parseInt(e.target.value)})}
                        >
                          {monthOptions.map(month => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                        <input
                          style={inputStyle}
                          type="number"
                          value={inputs.onetimeYear}
                          onChange={(e) => setInputs({...inputs, onetimeYear: parseInt(e.target.value) || 2026})}
                          min="2000"
                          max="2100"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  style={calculateButtonStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            <div style={glassCardStyle}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : '1.3rem', textAlign: 'left' as const }}>
                {t("summaryTitle")}
              </h3>
              {results && (
                <div>
                  <div style={{ ...summaryItemStyle, background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderLeftColor: '#f59e0b' }}>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{t("monthlyPaymentLabel")}</span>
                    <span style={{ fontWeight: 700, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem' }}>
                      {formatCurrency(results.monthlyPayment)}
                    </span>
                  </div>
                  <div style={summaryItemStyle}>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{t("totalPrincipalLabel")}</span>
                    <span style={{ fontWeight: 700, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem' }}>
                      {formatCurrency(results.totalPrincipal)}
                    </span>
                  </div>
                  <div style={summaryItemStyle}>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{t("totalInterestLabel")}</span>
                    <span style={{ fontWeight: 700, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem' }}>
                      {formatCurrency(results.totalInterest)}
                    </span>
                  </div>
                  <div style={summaryItemStyle}>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{t("totalPaidLabel")}</span>
                    <span style={{ fontWeight: 700, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem' }}>
                      {formatCurrency(results.totalPaid)}
                    </span>
                  </div>
                  <div style={summaryItemStyle}>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{t("payoffDateLabel")}</span>
                    <span style={{ fontWeight: 700, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem' }}>
                      {results.payoffDate ? formatDate(results.payoffDate) : '-'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {results && (
            <>
              <div style={glassCardStyle}>
                <h3 style={h2Style}>{t("paymentBreakdownTitle")}</h3>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '2rem 0' }}>
                  <svg width={screenSize === 'mobile' ? '250' : '300'} height={screenSize === 'mobile' ? '250' : '300'} viewBox="0 0 300 300">
                    <circle cx="150" cy="150" r="140" fill="#2563eb" />
                    <path
                      d={`M 150 150 L 150 10 A 140 140 0 ${((results.totalPrincipal / results.totalPaid) * 360) > 180 ? 1 : 0} 1 ${150 + 140 * Math.sin(((results.totalPrincipal / results.totalPaid) * 360) * Math.PI / 180)} ${150 - 140 * Math.cos(((results.totalPrincipal / results.totalPaid) * 360) * Math.PI / 180)} Z`}
                      fill="#f59e0b"
                    />
                    <circle cx="150" cy="150" r="60" fill="white" />
                    <text x="150" y="145" textAnchor="middle" fontSize="16" fontWeight="600" fill="#334155">{t("totalPaidText")}</text>
                    <text x="150" y="165" textAnchor="middle" fontSize="20" fontWeight="700" fill="#1e293b">{formatCurrency(results.totalPaid)}</text>
                  </svg>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: screenSize === 'mobile' ? '1rem' : '2rem', flexWrap: 'wrap' as const }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '4px', background: '#2563eb' }}></div>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{t("principalLegend")}:</span>
                    <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{formatCurrency(results.totalPrincipal)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '4px', background: '#f59e0b' }}></div>
                    <span style={{ fontWeight: 600, color: '#475569' }}>{t("interestLegend")}:</span>
                    <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{formatCurrency(results.totalInterest)}</span>
                  </div>
                </div>
              </div>

              <div style={glassCardStyle}>
                <h3 style={h2Style}>{t("scheduleTitle")}</h3>
                <div style={{ maxHeight: '500px', overflowY: 'auto' as const, overflowX: 'auto' as const }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: screenSize === 'mobile' ? '0.85rem' : '1rem' }}>
                    <thead style={{ position: 'sticky' as const, top: 0, background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left' as const, fontWeight: 600 }}>{t("paymentNumberHeader")}</th>
                        <th style={{ padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left' as const, fontWeight: 600 }}>{t("dateHeader")}</th>
                        <th style={{ padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left' as const, fontWeight: 600 }}>{t("paymentHeader")}</th>
                        <th style={{ padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left' as const, fontWeight: 600 }}>{t("principalHeader")}</th>
                        <th style={{ padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left' as const, fontWeight: 600 }}>{t("interestHeader")}</th>
                        <th style={{ padding: screenSize === 'mobile' ? '0.5rem' : '1rem', textAlign: 'left' as const, fontWeight: 600 }}>{t("balanceHeader")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.schedule.map((row: any, index: number) => (
                        <tr key={index} style={{ background: index % 2 === 0 ? '#fafafa' : 'transparent' }}>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{row.paymentNum}</td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{formatDate(row.date)}</td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{formatCurrency(row.payment)}</td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{formatCurrency(row.principal)}</td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{formatCurrency(row.interest)}</td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>{formatCurrency(row.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={paragraphStyle}>{t("howToStep1")}</li>
          <li style={paragraphStyle}>{t("howToStep2")}</li>
          <li style={paragraphStyle}>{t("howToStep3")}</li>
          <li style={paragraphStyle}>{t("howToStep4")}</li>
          <li style={paragraphStyle}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("detailsSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t("faqQuestion1")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t("faqQuestion2")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t("faqQuestion3")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t("faqQuestion4")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer4")}</p>
        </div>

        <div>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t("faqQuestion5")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer5")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ paddingLeft: '1.5rem', color: colors.textSecondary }}>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>
            <a href="https://smartasset.com/financial-advisor/amortization-table" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t("reference1")}
            </a>
          </li>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>
            <a href="https://www.santander.com/en/stories/amortization" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t("reference2")}
            </a>
          </li>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>
            <a href="https://www.fidelity.com/learning-center/personal-finance/what-is-amortization" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t("reference3")}
            </a>
          </li>
          <li style={{ ...paragraphStyle, marginBottom: '0.75rem' }}>
            <a href="https://www.americanexpress.com/en-us/credit-cards/credit-intel/loan-amortization/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t("reference4")}
            </a>
          </li>
          <li style={paragraphStyle}>
            <a href="https://myhome.freddiemac.com/owning/understanding-amortization" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 600 }}>
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}